import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { useModalStore } from '@/store/useModalStore.ts';
import classNames from 'classnames';
import styles from './Modal.module.scss';
import CloseIcon from '@/assets/Icons/Close.svg?react';

interface ModalHandlerProps {
	children: ReactNode;
	closeable?: boolean;
	className?: string;
	isClosing?: boolean;
	onClose?: () => void;
	outsideClickEnabled?: boolean;
}

interface ModalProps {
	name: string;
	children: ReactNode;
	closeable?: boolean;
	className?: string;
	onClose?: () => void;
	outsideClickEnabled?: boolean;
}

const ModalHandler: FC<ModalHandlerProps> = ({
	children,
	closeable = true,
	isClosing = false,
	onClose,
	className,
	outsideClickEnabled = true,
}) => {
	const closeModal = useModalStore(store => store.closeModal);
	const modalRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);

	const handleCloseModal = () => {
		if (isClosing) {
			return;
		}

		closeModal(onClose);
	};

	const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (outsideClickEnabled && e.target === modalRef.current) {
			handleCloseModal();
		}
	};

	useHotkeys('esc', handleCloseModal, {
		enabled: closeable,
	});

	useEffect(() => {
		document.body.style.overflowY = 'hidden';

		return () => {
			document.body.style.overflowY = 'auto';
		};
	}, []);

	useEffect(() => {
		const animationFrame = requestAnimationFrame(() => {
			setIsOpen(true);
		});

		return () => cancelAnimationFrame(animationFrame);
	}, []);

	const overlayStateClassName = isClosing
		? styles.modalOverlayExit
		: isOpen
			? styles.modalOverlayOpen
			: styles.modalOverlayEnter;
	const contentStateClassName = isClosing
		? styles.modalContentExit
		: isOpen
			? styles.modalContentOpen
			: styles.modalContentEnter;

	return ReactDOM.createPortal(
		<div className={styles.wrapper}>
			<div
				className={classNames(styles.modalOverlay, overlayStateClassName)}
				ref={modalRef}
				onClick={handleOverlayClick}
			>
				<div className={classNames(styles.modalContent, contentStateClassName, className)}>
					{children}
				</div>
				{closeable && (
					<button onClick={handleCloseModal} className={styles.closeButton} disabled={isClosing}>
						<CloseIcon />
					</button>
				)}
			</div>
		</div>,
		document.body,
	);
};

const Modal: FC<ModalProps> = ({ name, children, closeable = true, onClose, className, outsideClickEnabled }) => {
	const activeModal = useModalStore(store => store.activeModal);
	const closingModal = useModalStore(store => store.closingModal);
	const isModalMounted = activeModal === name || closingModal === name;

	if (!isModalMounted) {
		return null;
	}

	return (
		<ModalHandler
			closeable={closeable}
			className={className}
			isClosing={closingModal === name}
			onClose={onClose}
			outsideClickEnabled={outsideClickEnabled}
		>
			{children}
		</ModalHandler>
	);
};

export default Modal;
